import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { DEFAULT_INSTRUMENT_ID, getInstrument, ratingLevel } from '@/lib/schema/loader';
import { scoreInstrument } from '@/lib/scoring/totalScore';
import { useObservation } from '@/lib/state/useObservation';
import { downloadObservationJson } from '@/lib/export/toJson';
import { exportObservationPdf } from '@/lib/export/toPdf';
import {
  syncIsConfigured,
  syncObservation,
  type SyncResult,
} from '@/lib/sync/lambdaClient';

export function SummaryView() {
  const instrument = getInstrument(DEFAULT_INSTRUMENT_ID)!;
  const { state, markSubmitted } = useObservation();
  const result = useMemo(
    () => scoreInstrument(instrument, state.responses, state.itemNa),
    [instrument, state.responses, state.itemNa],
  );

  return (
    <AppShell instrumentId={instrument.id} showSidebar={false}>
      <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="font-serif text-3xl font-bold text-dece-navy">
              Observation summary
            </h2>
            <p className="text-sm text-muted-foreground">
              Computed scores for {instrument.short_name} ·{' '}
              {state.metadata.siteName || 'Untitled site'} ·{' '}
              {state.metadata.observationDate}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Total scale score
            </div>
            <div className="font-serif text-5xl font-bold text-dece-navy">
              {result.average != null ? result.average.toFixed(2) : '—'}
            </div>
            <div className="text-xs text-muted-foreground">
              {result.scoredItems} scored · {result.naItems} NA ·{' '}
              {result.pendingItems} pending
            </div>
          </div>
        </header>

        <div className="space-y-4">
          {result.subscales.map((s) => (
            <section
              key={s.id}
              className="rounded-lg border bg-card p-4"
              aria-labelledby={`summary-${s.id}`}
            >
              <div className="flex items-baseline justify-between">
                <h3
                  id={`summary-${s.id}`}
                  className="font-serif text-xl font-semibold text-dece-navy"
                >
                  {s.name}
                </h3>
                <div className="font-serif text-3xl font-bold text-dece-navy">
                  {s.result.average != null ? s.result.average.toFixed(2) : '—'}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {s.result.itemResults.map(({ item, score, itemNa, complete }) => (
                  <ScoreTile
                    key={item.id}
                    observationId={state.id}
                    item={item}
                    score={itemNa ? 'NA' : score}
                    complete={complete}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" asChild>
            <Link
              to={
                instrument.subscales[0]?.items[0]
                  ? `/observation/${state.id}/items/${instrument.subscales[0].items[0].id}`
                  : '/'
              }
            >
              Back to observation
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadObservationJson(instrument, state)}
          >
            Export JSON
          </Button>
          <Button
            variant="outline"
            onClick={() => exportObservationPdf(instrument, state)}
          >
            Export PDF
          </Button>
          <SyncButton
            onSuccess={() => markSubmitted()}
            instrument={instrument}
            record={state}
            alreadySubmitted={state.status === 'submitted'}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Exports contain observer name, email, site address and any
          free-text notes. Handle with the same care as the paper form.
        </p>
      </div>
    </AppShell>
  );
}

function SyncButton({
  instrument,
  record,
  alreadySubmitted,
  onSuccess,
}: {
  instrument: NonNullable<ReturnType<typeof getInstrument>>;
  record: ReturnType<typeof useObservation>['state'];
  alreadySubmitted: boolean;
  onSuccess: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const configured = syncIsConfigured();

  if (!configured) {
    return (
      <Button
        disabled
        title="Set VITE_SYNC_URL at build time to enable Google Sheets sync."
      >
        Submit
      </Button>
    );
  }

  const run = async () => {
    setBusy(true);
    setResult(null);
    const r = await syncObservation(instrument, record);
    setResult(r);
    if (r.ok) onSuccess();
    setBusy(false);
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        onClick={run}
        disabled={busy || alreadySubmitted}
        title={
          alreadySubmitted
            ? 'This observation has already been submitted.'
            : undefined
        }
      >
        {busy
          ? 'Submitting…'
          : alreadySubmitted
            ? 'Submitted ✓'
            : 'Submit'}
      </Button>
      {result && !result.ok ? (
        <span className="text-xs text-destructive">{result.error}</span>
      ) : null}
      {result?.ok ? (
        <span className="text-xs text-rating-5">
          {result.duplicate
            ? 'Already recorded (no duplicate row).'
            : result.sheetRow != null
              ? `Saved to Sheet row ${result.sheetRow}.`
              : 'Saved.'}
        </span>
      ) : null}
    </div>
  );
}

function ScoreTile({
  observationId,
  item,
  score,
  complete,
}: {
  observationId: string;
  item: { id: string; number: number; name: string };
  score: number | null | 'NA';
  complete: boolean;
}) {
  const meta =
    typeof score === 'number' ? ratingLevel(Math.round(score)) : undefined;
  const label = `${item.number}. ${item.name}`;
  const ariaLabel =
    score === 'NA'
      ? `${label} — Not Applicable. Click to reopen.`
      : score == null
        ? `${label} — not yet scored. Click to continue scoring.`
        : `${label} — score ${score} (${meta?.label ?? ''}). Click to review.`;
  return (
    <Link
      to={`/observation/${observationId}/items/${item.id}`}
      aria-label={ariaLabel}
      className="flex items-center gap-2 rounded-md border p-2 text-xs transition hover:bg-accent/60 hover:ring-2 hover:ring-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md text-sm font-bold text-white"
        style={{
          backgroundColor:
            score == null
              ? 'hsl(var(--muted))'
              : score === 'NA'
                ? 'hsl(var(--muted-foreground))'
                : meta?.color_hex,
        }}
      >
        {score == null ? '—' : score === 'NA' ? 'NA' : score}
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate">{label}</div>
        {!complete && score !== 'NA' ? (
          <div className="text-[10px] uppercase tracking-wide text-amber-700">
            Needs review
          </div>
        ) : null}
      </div>
    </Link>
  );
}

